import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
    AlertTriangle, Check, ChevronDown, ChevronUp, Copy, QrCode, RefreshCw, Unlink, X,
} from 'lucide-react';
import type { ExterneFreimeldung, FreimeldungLink } from '../../types';
import { freimeldungLinkUrl, istZurueckweisung } from '../../services/freimeldungLogic';

/**
 * Die Schaltzentrale der externen Freimeldung IM Begehungsmodul
 * (docs/freimeldung-online-plan.md §F).
 *
 * Sie beantwortet zwei Fragen, die an keiner Mangelkarte stehen koennen:
 *
 * (a) WELCHER LINK GEHOERT ZU WELCHEM BERICHT? Der QR-Code steht im
 *     versendeten PDF. Wer ihn noch einmal braucht — Anruf einer Firma, die
 *     das PDF nicht findet —, holt Link und QR hier, statt den Bericht erneut
 *     zu versenden.
 *
 * (b) WO LANDEN MELDUNGEN ZU MAENGELN, DIE ES NICHT MEHR GIBT? Ohne
 *     Fremdschluessel auf `inspection_items` (der Id-Typ steht nicht im Repo)
 *     kann ein Mangel geloescht oder in ein anderes Projekt gezogen werden,
 *     waehrend seine Meldung offen bleibt. Sie haette sonst keine Karte mehr,
 *     auf der sie erschiene, und stuende fuer immer im Zaehler.
 *
 * FAIL-OPEN: Ohne Migration 108/109 liefern die Services leere Listen — dann
 * ist das Panel unsichtbar und niemand sieht eine Funktion, die es nicht gibt.
 */

interface Props {
    /** Ein Link je versendetem Bericht (neueste Nummer zuerst). */
    links: FreimeldungLink[];
    /** Offene Meldungen zu Maengeln, die im Projekt nicht mehr existieren. */
    verwaiste: ExterneFreimeldung[];
    /** Alle offenen Meldungen des Projekts — auch die an den Mangelkarten. */
    offene: number;
    laedt?: boolean;
    /** Fehler des LADENS — steht im aufgeklappten Teil. */
    fehler?: string | null;
    /**
     * Fehler der letzten ENTSCHEIDUNG. Steht im Kopf und damit auch dann, wenn
     * das Panel zugeklappt ist: sonst verschwindet die einzige Nachricht
     * darueber, dass eine Buchung nicht ankam, hinter einem Pfeil.
     */
    entscheidungsFehler?: string | null;
    onReload: () => void;
    /** Bucht die Entscheidung SOFORT (RPC). Hier nur noch fuer „Ablehnen". */
    onEntscheiden: (id: string, entscheidung: 'bestaetigt' | 'abgelehnt', kommentar?: string) => Promise<void>;
    /**
     * Reiht eine Bestaetigung ein, die erst gebucht werden darf, wenn der
     * Mangel NACHWEISLICH nicht mehr offen in der Datenbank steht
     * (docs/freimeldung-online-plan.md §F) — derselbe Weg wie an der
     * Mangelkarte.
     *
     * WARUM AUCH HIER, WO ES DEN MANGEL DOCH GAR NICHT MEHR GIBT: „Verwaist"
     * ist ein LOKALER Befund — der Mangel fehlt in `report.items` dieser
     * Ansicht. Ob sein Loeschen (oder sein Umzug in ein anderes Projekt)
     * ueberhaupt schon hochgeladen ist, weiss diese Liste nicht: der Save
     * laeuft entprellt und kann scheitern. Sofort gebucht, stuende die Meldung
     * auf „bestaetigt" — und mit ihr, ueber die RPC, alle Geschwister
     * desselben Mangels —, waehrend der Mangel serverseitig weiter offen ist.
     * Der Nachweis lautet hier `fehlt` (Zeile fort) oder `sonstig`.
     *
     * Fehlt der Handler, faellt „Erledigt" auf `onEntscheiden` zurueck; das
     * Verhalten ausserhalb des Begehungsmoduls bleibt damit, wie es war.
     */
    onNachSpeichern?: (
        id: string,
        itemId: string,
        entscheidung: 'bestaetigt' | 'abgelehnt',
        kommentar?: string,
    ) => void;
    isReadOnly?: boolean;
}

const tagText = (iso: string | null | undefined): string => {
    const roh = (iso || '').trim();
    if (!roh) return '';
    const ms = Date.parse(roh);
    if (Number.isNaN(ms)) return roh;
    return new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Leeres `aktiv_bis` heisst unbefristet — dieselbe Regel wie im Service. */
const istAbgelaufen = (aktivBis: string | null): boolean => {
    if (!aktivBis) return false;
    const ms = Date.parse(aktivBis);
    return Number.isNaN(ms) ? false : ms <= Date.now();
};

export const FreimeldungPanel: React.FC<Props> = ({
    links, verwaiste, offene, laedt = false, fehler = null, entscheidungsFehler = null,
    onReload, onEntscheiden, onNachSpeichern, isReadOnly = false,
}) => {
    // Aufgeklappt, sobald etwas zu tun ist: verwaiste Meldungen haben keine
    // andere Stelle, an der sie auffielen.
    //
    // ABGELEITET, NICHT EINMALIG GESETZT: Beim Oeffnen des Moduls laeuft der
    // Abruf der Meldungen noch, `verwaiste` ist dann leer. Ein
    // useState-Startwert bliebe deshalb fuer immer auf `false` stehen und die
    // Selbstoeffnung fiele genau in dem Fall aus, fuer den es sie gibt.
    // `manuell` bleibt `null`, bis der Koordinator den Kopf anklickt — ab dann
    // gilt nur noch seine Entscheidung.
    const [manuell, setManuell] = useState<boolean | null>(null);
    const offen = manuell ?? verwaiste.length > 0;
    const [kopiertId, setKopiertId] = useState<string | null>(null);
    const [qr, setQr] = useState<{ id: string; dataUrl: string; url: string } | null>(null);
    const [qrFehler, setQrFehler] = useState<string | null>(null);

    // ═══ DIE UHR HINTER „KOPIERT ✓" ════════════════════════════════════════
    //
    // Sie lief bisher ohne Kennung: niemand konnte sie abbrechen. Wer kopiert
    // und sofort weiterklickt (Projekt zu, Modul zu), lief in einen Setter auf
    // einer Ansicht, die es nicht mehr gibt; zwei Kopien kurz nacheinander
    // liessen ausserdem zwei Uhren nebeneinander laufen, und die erste loeschte
    // die Rueckmeldung der zweiten zu frueh. Die Kennung liegt im Ref, damit
    // sie beim Demontieren und vor jeder neuen Uhr abgeraeumt werden kann.
    //
    // OBERHALB DES FRUEHEN RETURNS: Hooks muessen bei jedem Anstrich in
    // derselben Reihenfolge laufen — das Panel verschwindet, sobald nichts mehr
    // offen ist.
    const kopiertUhr = useRef<number | null>(null);
    const montiert = useRef(true);
    useEffect(() => {
        montiert.current = true;
        return () => {
            montiert.current = false;
            if (kopiertUhr.current !== null) {
                window.clearTimeout(kopiertUhr.current);
                kopiertUhr.current = null;
            }
        };
    }, []);

    // Nichts da, nichts gemeldet: das Modul sieht aus wie vorher. Ein
    // gescheiterter Buchungsversuch haelt das Panel trotzdem sichtbar — sonst
    // verschwaende die Nachricht mit dem Zaehler, auf den sie sich bezieht.
    if (!links.length && !verwaiste.length && offene <= 0 && !entscheidungsFehler) return null;

    // Nur fuer die Farbe der Kopfzeile: gelb wird sie erst, wenn etwas offen
    // oder verwaist ist. Nicht mit `offen` (aufgeklappt) verwechseln.
    const ruhig = offene <= 0 && verwaiste.length === 0;

    const urlZu = (link: FreimeldungLink): string =>
        freimeldungLinkUrl(window.location.origin, window.location.pathname, link.token);

    const kopieren = async (link: FreimeldungLink) => {
        const url = urlZu(link);
        try {
            await navigator.clipboard.writeText(url);
            // Nach dem `await` kann das Panel fort sein — dann gibt es nichts
            // mehr zurueckzumelden.
            if (!montiert.current) return;
            setKopiertId(link.id);
            // Erst die alte Uhr weg, dann die neue: sonst raeumt der Nachlauf
            // des vorigen Klicks diese Rueckmeldung vorzeitig ab.
            if (kopiertUhr.current !== null) window.clearTimeout(kopiertUhr.current);
            kopiertUhr.current = window.setTimeout(() => {
                kopiertUhr.current = null;
                setKopiertId((id) => (id === link.id ? null : id));
            }, 2000);
        } catch {
            // Ohne Zwischenablage-Recht (Firefox, http) bleibt der Weg von Hand.
            window.prompt('Link kopieren:', url);
        }
    };

    const qrZeigen = async (link: FreimeldungLink) => {
        if (qr?.id === link.id) { setQr(null); return; }
        const url = urlZu(link);
        setQrFehler(null);
        try {
            const dataUrl = await QRCode.toDataURL(url, { width: 480, margin: 2 });
            setQr({ id: link.id, dataUrl, url });
        } catch (e) {
            console.warn('QR-Code konnte nicht erzeugt werden', e);
            setQrFehler('Der QR-Code ließ sich nicht erzeugen. Der Link oben funktioniert trotzdem.');
        }
    };

    /**
     * „Erledigt" an einer verwaisten Meldung — EINGEREIHT, nicht sofort
     * gebucht. Die Mangel-Id haengt an der Meldung selbst (`item_id` steht in
     * der Zeile, auch wenn der Mangel in dieser Ansicht fehlt); ohne sie waere
     * der Nachweis nicht zu fuehren.
     *
     * Ohne `onNachSpeichern` bleibt der alte, sofortige Weg — dann gibt es
     * keine Warteschlange, die die Entscheidung spaeter nachholen koennte.
     */
    const erledigen = (f: ExterneFreimeldung) => {
        if (onNachSpeichern) {
            onNachSpeichern(f.id, f.itemId, 'bestaetigt');
            return;
        }
        void onEntscheiden(f.id, 'bestaetigt');
    };

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setManuell(!offen)}
                className={`w-full min-h-11 px-4 py-2.5 flex items-center gap-2 text-left transition-colors ${ruhig ? 'bg-white hover:bg-slate-50' : 'bg-amber-100 hover:bg-amber-200 active:bg-amber-200'}`}
            >
                <span className="min-w-0 truncate font-semibold text-sm text-slate-800">Online-Freimeldungen</span>
                <span className={`ml-auto shrink-0 whitespace-nowrap text-xs ${ruhig ? 'text-slate-400' : offene > 0 ? 'font-semibold text-amber-700' : 'text-amber-700'}`}>
                    {offene > 0 ? `${offene} offen` : 'nichts offen'}
                </span>
                {verwaiste.length > 0 && (
                    <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-rose-700">
                        {verwaiste.length} ohne Mangel
                    </span>
                )}
                <span className={`shrink-0 ${ruhig ? 'text-slate-400' : 'text-amber-700'}`}>{offen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
            </button>

            {/* Im Kopf, nicht im Klappteil: sichtbar auch zugeklappt. */}
            {entscheidungsFehler && (
                <p className="flex items-start gap-1.5 px-5 py-2 bg-rose-50 border-t border-rose-200 text-[11px] font-medium text-rose-700">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                    <span>Hinweis zur Entscheidung: {entscheidungsFehler}</span>
                </p>
            )}

            {offen && (
                <div className="p-5 space-y-5">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onReload}
                            disabled={laedt}
                            className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-[11px] font-bold uppercase tracking-wider hover:bg-slate-50 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${laedt ? 'animate-spin' : ''}`} /> Aktualisieren
                        </button>
                        <p className="text-[11px] text-slate-400">
                            Meldungen kommen über den QR-Code im versendeten Bericht herein. Sie schließen keinen Mangel — Sie entscheiden an der Mangelkarte.
                        </p>
                    </div>

                    {fehler && (
                        <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{fehler}</p>
                    )}

                    {/* ─── Links je Bericht ─────────────────────────────── */}
                    {links.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Links je Bericht</h4>
                            {links.map((link) => {
                                const abgelaufen = istAbgelaufen(link.aktivBis);
                                const url = urlZu(link);
                                return (
                                    <div key={link.id} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/60">
                                        <div className="flex flex-wrap items-center gap-2 text-sm">
                                            <span className="font-bold text-slate-800">Bericht Nr. {link.inspectionNumber}</span>
                                            {link.reportDate && <span className="text-[11px] text-slate-400">vom {tagText(link.reportDate)}</span>}
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${abgelaufen ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                                                {abgelaufen ? 'abgelaufen' : 'aktiv'}
                                            </span>
                                            <span className="text-[11px] text-slate-400">{link.itemIds.length} Mängel im PDF</span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <code className="flex-1 min-w-0 truncate text-[11px] text-slate-500 bg-white border border-slate-200 rounded px-2 py-1">{url}</code>
                                            <button
                                                type="button"
                                                onClick={() => void kopieren(link)}
                                                className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 transition-colors flex items-center gap-1.5"
                                            >
                                                {kopiertId === link.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                                {kopiertId === link.id ? 'Kopiert' : 'Kopieren'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void qrZeigen(link)}
                                                className="px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 text-[11px] font-bold hover:bg-slate-50 transition-colors flex items-center gap-1.5"
                                            >
                                                <QrCode className="w-3.5 h-3.5" /> {qr?.id === link.id ? 'QR ausblenden' : 'QR anzeigen'}
                                            </button>
                                        </div>
                                        {qr?.id === link.id && (
                                            <div className="pt-1">
                                                <img src={qr.dataUrl} alt={`QR-Code Bericht ${link.inspectionNumber}`} className="w-40 h-40 border border-slate-200 rounded-lg bg-white" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {qrFehler && <p className="text-[11px] text-rose-600">{qrFehler}</p>}
                        </div>
                    )}

                    {/* ─── Verwaiste Meldungen ──────────────────────────── */}
                    {verwaiste.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-[11px] font-bold text-rose-500 uppercase tracking-widest flex items-center gap-1.5">
                                <Unlink className="w-3.5 h-3.5" /> Mangel nicht mehr vorhanden
                            </h4>
                            <p className="text-[11px] text-slate-400">
                                Diese Meldungen gehören zu Mängeln, die es in diesem Projekt nicht mehr gibt. Sie lassen sich nur hier abschließen.
                            </p>
                            {verwaiste.map((f) => (
                                <div key={f.id} className="border border-rose-100 bg-rose-50/50 rounded-xl p-3 space-y-2">
                                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                                        {istZurueckweisung(f) && (
                                            <span className="self-center px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">Zurückgewiesen</span>
                                        )}
                                        {f.ticketNumber != null && <span className="font-black text-rose-700">#{f.ticketNumber}</span>}
                                        <span className="font-bold text-slate-800">{f.itemTitle || 'ohne Titel'}</span>
                                        <span className="text-[11px] text-slate-500">· {f.firma}</span>
                                        {f.name && <span className="text-[11px] text-slate-500">· {f.name}</span>}
                                        {f.gemeldetAt && <span className="text-[11px] text-slate-400">· {tagText(f.gemeldetAt)}</span>}
                                    </div>
                                    {f.kommentar && <p className="text-sm text-slate-700 whitespace-pre-wrap">{f.kommentar}</p>}
                                    {!isReadOnly && (
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => erledigen(f)}
                                                className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-green-700 transition-colors flex items-center gap-1.5"
                                            >
                                                <Check className="w-3.5 h-3.5" /> Erledigt
                                            </button>
                                            {/* „Ablehnen" bleibt SOFORT: hier wird kein Mangel
                                                geschlossen, es gibt also nichts nachzuweisen. */}
                                            <button
                                                type="button"
                                                onClick={() => void onEntscheiden(f.id, 'abgelehnt', 'Mangel in diesem Projekt nicht mehr vorhanden.')}
                                                className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-600 text-[11px] font-bold uppercase tracking-wider hover:bg-rose-50 transition-colors flex items-center gap-1.5"
                                            >
                                                <X className="w-3.5 h-3.5" /> Ablehnen
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
